import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowUpDown,
  CheckCircle2,
  Search,
  Shuffle,
  Trophy,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { DrawType, rewardsService } from '@/services/rewards.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ExecuteDrawModalProps {
  open: boolean;
  onClose: () => void;
  rewardId: string;
  rewardTitle: string;
}

type SortField =
  | 'userName'
  | 'tokensBid'
  | 'lotteryNumber'
  | 'participationDate';
type SortOrder = 'asc' | 'desc';

interface RewardParticipation {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  tokensBid: number;
  lotteryNumber: number;
  participationDate: string;
}

interface DrawWinner {
  id: string;
  winningRank?: number;
  userName: string;
  userEmail: string;
  tokensBid?: number;
}

interface DrawExecutionResult {
  drawExecutedAt: string;
  drawType: DrawType;
  totalParticipants: number;
  totalTokensBid: number;
  winners: DrawWinner[];
}

export function ExecuteDrawModal({
  open,
  onClose,
  rewardId,
  rewardTitle,
}: ExecuteDrawModalProps) {
  const queryClient = useQueryClient();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [executionResult, setExecutionResult] = useState<DrawExecutionResult | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('participationDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Fetch reward details
  const { data: reward } = useQuery({
    queryKey: ['reward-details', rewardId],
    queryFn: () => rewardsService.getReward(rewardId),
    enabled: open,
  });

  // Fetch participations for manual draw
  const { data: participations } = useQuery({
    queryKey: ['reward-participations', rewardId],
    queryFn: () =>
      rewardsService.getParticipations(rewardId, {
        maxResultCount: 1000,
        skipCount: 0,
      }),
    enabled: open && reward?.drawType === DrawType.Manual,
  });

  // Execute draw mutation
  const executeMutation = useMutation({
    mutationFn: (selectedIds?: string[]) =>
      rewardsService.executeDraw(
        rewardId,
        selectedIds ? { selectedUserIds: selectedIds } : undefined,
      ),
    onSuccess: (result) => {
      toast.success('Draw executed successfully!');
      const totalTokensBid = result.winners.reduce(
        (sum, winner) => sum + (winner.tokensBid ?? 0),
        0,
      );
      setExecutionResult({ ...result, totalTokensBid });
      queryClient.invalidateQueries({ queryKey: ['rewards'] });
      queryClient.invalidateQueries({ queryKey: ['reward-details', rewardId] });
      queryClient.invalidateQueries({ queryKey: ['reward-winners', rewardId] });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to execute draw';
      toast.error(message);
    },
  });

  const handleExecuteDraw = () => {
    if (reward?.drawType === DrawType.Manual && selectedUserIds.length === 0) {
      toast.error('Please select at least one participant');
      return;
    }

    if (
      reward?.drawType === DrawType.Manual &&
      selectedUserIds.length !== reward?.winnerCount
    ) {
      if (
        !confirm(
          `You selected ${selectedUserIds.length} winners but the reward requires ${reward.winnerCount}. Continue anyway?`,
        )
      ) {
        return;
      }
    }

    if (
      !confirm(
        `Are you sure you want to execute the draw for "${rewardTitle}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    executeMutation.mutate(
      reward?.drawType === DrawType.Manual ? selectedUserIds : undefined,
    );
  };

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleSelectAll = () => {
    if (!participations) return;
    if (selectedUserIds.length === participations.items.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(participations.items.map((p) => p.userId));
    }
  };

  const handleSelectRandom = () => {
    if (!participations || !reward) return;
    const shuffled = [...participations.items]
      .sort(() => Math.random() - 0.5)
      .slice(0, reward.winnerCount);
    setSelectedUserIds(shuffled.map((p) => p.userId));
    toast.success(`Randomly selected ${shuffled.length} participants`);
  };

  const handleSelectTopByTokens = () => {
    if (!participations || !reward) return;
    const sorted = [...participations.items]
      .sort((a, b) => b.tokensBid - a.tokensBid)
      .slice(0, reward.winnerCount);
    setSelectedUserIds(sorted.map((p) => p.userId));
    toast.success(`Selected top ${sorted.length} by tokens bid`);
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    setExecutionResult(null);
    setSearchTerm('');
    setSortField('participationDate');
    setSortOrder('desc');
    onClose();
  };

  // Filter and sort participants
  const filteredAndSortedParticipants = useMemo(() => {
    if (!participations) return [];

    const participants = (participations?.items ?? []) as RewardParticipation[];
    let filtered = participants;

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.userName.toLowerCase().includes(search) ||
          p.userEmail.toLowerCase().includes(search),
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aVal: string | number = a[sortField];
      let bVal: string | number = b[sortField];

      if (sortField === 'participationDate') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  }, [participations, searchTerm, sortField, sortOrder]);

  if (!reward) {
    return null;
  }

  // Show results if draw was executed
  if (executionResult) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-6 w-6 text-yellow-500" />
              Draw Executed Successfully!
            </DialogTitle>
            <DialogDescription>
              Winners have been selected and prizes have been awarded
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-secondary rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">
                  Total Participants
                </p>
                <p className="text-3xl font-bold">
                  {executionResult.totalParticipants.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">
                  Winners Selected
                </p>
                <p className="text-3xl font-bold text-success">
                  {executionResult.winners.length}
                </p>
              </div>
              <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">
                  Total Prizes Awarded
                </p>
                <p className="text-3xl font-bold text-primary">
                  {(executionResult.winners.length * 200).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  T-Spark Tokens
                </p>
              </div>
            </div>

            {/* Draw Info */}
            <div className="flex items-center justify-between p-3 bg-secondary rounded-lg text-sm">
              <div>
                <span className="text-muted-foreground">Draw Type: </span>
                <span className="font-medium">
                  {executionResult.drawType === DrawType.Automatic
                    ? 'Automatic (Random)'
                    : 'Manual Selection'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Executed At: </span>
                <span className="font-medium">
                  {new Date(executionResult.drawExecutedAt).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Winners Table */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Trophy className="h-4 w-4 text-yellow-500" />
                Winners List
              </h3>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">Rank</TableHead>
                      <TableHead>User Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Tokens Bid</TableHead>
                      <TableHead className="text-right">
                        Prize Awarded
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {executionResult.winners.map(
                      (winner: DrawWinner, index: number) => (
                        <TableRow key={winner.id} className="hover:bg-muted/50">
                          <TableCell>
                            <Badge
                              variant={index === 0 ? 'secondary' : 'secondary'}
                              className={
                                index === 0
                                  ? 'bg-yellow-500 hover:bg-yellow-600'
                                  : index === 1
                                    ? 'bg-gray-400 hover:bg-gray-500'
                                    : index === 2
                                      ? 'bg-amber-600 hover:bg-amber-700'
                                      : ''
                              }
                            >
                              #{winner.winningRank || index + 1}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">
                            {winner.userName}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {winner.userEmail}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {winner.tokensBid?.toLocaleString() || 0}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge
                              variant="secondary"
                              className="bg-success/10 text-success"
                            >
                              200 Tokens
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Success Message */}
            <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/20 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-success mb-1">
                  Draw Completed
                </p>
                <p className="text-muted-foreground">
                  All winners have been automatically awarded their prizes. The
                  reward status has been updated to "Drawn" and no more
                  participants can join.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Show draw execution form
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Execute Draw: {rewardTitle}</DialogTitle>
          <DialogDescription>
            {reward.drawType === DrawType.Automatic
              ? 'This will automatically select winners randomly from all participants.'
              : 'Manually select the winners from the list of participants below.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-secondary rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Participants</p>
              </div>
              <p className="text-2xl font-bold">{reward.currentParticipants}</p>
            </div>
            <div className="p-4 bg-secondary rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Trophy className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Winners Needed</p>
              </div>
              <p className="text-2xl font-bold text-primary">
                {reward.winnerCount}
              </p>
            </div>
            <div className="p-4 bg-secondary rounded-lg">
              <p className="text-sm text-muted-foreground">Draw Type</p>
              <p className="text-lg font-semibold">
                {reward.drawType === DrawType.Automatic
                  ? 'Automatic (Random)'
                  : 'Manual Selection'}
              </p>
            </div>
          </div>

          {/* Automatic Draw Info */}
          {reward.drawType === DrawType.Automatic && (
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Shuffle className="h-5 w-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">Random Selection</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    The system will automatically select{' '}
                    <strong>{reward.winnerCount}</strong> winner(s) randomly
                    from all <strong>{reward.currentParticipants}</strong>{' '}
                    participants. Each participant has an equal chance of
                    winning regardless of tokens bid.
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">
                        Selection Method:
                      </span>
                      <p className="font-medium">Random (Fair)</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        Prize per Winner:
                      </span>
                      <p className="font-medium">200 T-Spark Tokens</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Manual Selection */}
          {reward.drawType === DrawType.Manual && participations && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">
                  Select Winners ({selectedUserIds.length} of{' '}
                  {reward.winnerCount} needed)
                </h3>
                <div className="flex items-center gap-2">
                  {selectedUserIds.length === reward.winnerCount && (
                    <Badge
                      variant="secondary"
                      className="bg-success/10 text-success"
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Selection Complete
                    </Badge>
                  )}
                  {selectedUserIds.length > reward.winnerCount && (
                    <Badge
                      variant="secondary"
                      className="bg-warning/10 text-warning"
                    >
                      {selectedUserIds.length - reward.winnerCount} more than
                      needed
                    </Badge>
                  )}
                </div>
              </div>

              {/* Quick Selection Tools */}
              <div className="flex items-center gap-2 p-3 bg-secondary rounded-lg">
                <span className="text-sm font-medium">Quick Select:</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectRandom}
                  disabled={!participations.items.length}
                >
                  <Shuffle className="w-3 h-3 mr-2" />
                  Random {reward.winnerCount}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectTopByTokens}
                  disabled={!participations.items.length}
                >
                  <Trophy className="w-3 h-3 mr-2" />
                  Top by Tokens
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                  disabled={!participations.items.length}
                >
                  {selectedUserIds.length === participations.items.length
                    ? 'Deselect All'
                    : 'Select All'}
                </Button>
                {selectedUserIds.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedUserIds([])}
                  >
                    Clear Selection
                  </Button>
                )}
              </div>

              {/* Search and Sort */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select
                  value={sortField}
                  onValueChange={(value) => setSortField(value as SortField)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="userName">Name</SelectItem>
                    <SelectItem value="tokensBid">Tokens Bid</SelectItem>
                    <SelectItem value="lotteryNumber">
                      Lottery Number
                    </SelectItem>
                    <SelectItem value="participationDate">Join Date</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() =>
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                  }
                >
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </div>

              {/* Participants Table */}
              <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={
                            filteredAndSortedParticipants.length > 0 &&
                            filteredAndSortedParticipants.every((p) =>
                              selectedUserIds.includes(p.userId),
                            )
                          }
                          onCheckedChange={() => {
                            if (
                              filteredAndSortedParticipants.every((p) =>
                                selectedUserIds.includes(p.userId),
                              )
                            ) {
                              // Deselect all visible
                              setSelectedUserIds((prev) =>
                                prev.filter(
                                  (id) =>
                                    !filteredAndSortedParticipants.some(
                                      (p) => p.userId === id,
                                    ),
                                ),
                              );
                            } else {
                              // Select all visible
                              const newIds = filteredAndSortedParticipants.map(
                                (p) => p.userId,
                              );
                              setSelectedUserIds((prev) => [
                                ...new Set([...prev, ...newIds]),
                              ]);
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Tokens Bid</TableHead>
                      <TableHead className="text-right">Lottery #</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedParticipants.length > 0 ? (
                      filteredAndSortedParticipants.map((participant) => (
                        <TableRow
                          key={participant.id}
                          className={
                            selectedUserIds.includes(participant.userId)
                              ? 'bg-primary/5'
                              : ''
                          }
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedUserIds.includes(
                                participant.userId,
                              )}
                              onCheckedChange={() =>
                                handleToggleUser(participant.userId)
                              }
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {participant.userName}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {participant.userEmail}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {participant.tokensBid.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {participant.lotteryNumber || '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center py-8 text-muted-foreground"
                        >
                          {searchTerm
                            ? 'No participants found matching your search'
                            : 'No participants yet'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Warning */}
          <div className="flex items-start gap-2 p-4 bg-warning/10 border border-warning rounded-lg">
            <AlertCircle className="h-5 w-5 text-warning mt-0.5" />
            <div className="text-sm flex-1">
              <p className="font-semibold text-warning mb-1">
                Important Notice
              </p>
              <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                <li>
                  Executing this draw is <strong>irreversible</strong>
                </li>
                <li>Winners will receive 200 T-Spark tokens automatically</li>
                <li>Reward status will be updated to "Drawn"</li>
                <li>No more participants can join after execution</li>
              </ul>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={executeMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExecuteDraw}
            disabled={
              executeMutation.isPending ||
              (reward.drawType === DrawType.Manual &&
                selectedUserIds.length === 0) ||
              reward.currentParticipants === 0
            }
          >
            {executeMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Executing Draw...
              </>
            ) : (
              <>
                <Trophy className="w-4 h-4 mr-2" />
                Execute Draw
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
